// Admin dashboard for operators (OPERATOR_EMAILS): usage across everyone, the Phase 1 gate,
// and each user's AI credits, where an admin can give extra credits or raise limits.
import { useEffect, useState, type FormEvent } from "react";
import { EARLY_USERS, GRANT_DURATIONS, type GrantDuration } from "../../shared/ai-credits";
import { formatDate } from "../../shared/dates";
import { Alert, Badge, Button, Card, CardTitle, Dialog, Field, PageHeader, Select, Spinner, TextInput, cx, formatBytes } from "../components/ui";
import {
  DURATION_LABELS,
  addGrant,
  getGate,
  getOverview,
  grantLine,
  makePermanent,
  revokeGrant,
  searchUsers,
  updateWorkspace,
  gbToBytes,
  GB,
  type AdminOverview,
  type AdminUser,
  type AdminWorkspace,
  type GateMetrics,
  type GrantRequest,
} from "../lib/admin";
import { useOnline, useSession } from "../lib/session";
import { CreditBar } from "./Settings";

const n = (v: number) => v.toLocaleString("en-IN");
const inr = (v: number) => `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errorText = (err: unknown) => (err instanceof Error ? err.message : "Something went wrong.");

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-4 py-3">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums tracking-tight text-fg">{value}</p>
      {hint && <p className="mt-0.5 text-xs text-faint">{hint}</p>}
    </div>
  );
}

/** Credits used per day: one series, so one colour and no legend. */
function DailyChart({ daily }: { daily: AdminOverview["ai"]["daily"] }) {
  const max = Math.max(1, ...daily.map((d) => d.credits));
  return (
    <figure className="space-y-2">
      <div className="flex h-32 items-end gap-0.5 border-b border-line" role="img" aria-label={`Credits used per day, last ${daily.length} days`}>
        {daily.map((d) => (
          <div key={d.day} className="group relative flex h-full flex-1 items-end">
            <div
              className="w-full rounded-t-[4px] bg-accent transition-opacity group-hover:opacity-80"
              style={{
                height: `${d.credits ? Math.max(2, (d.credits / max) * 100) : 0}%`,
              }}
            />
            <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-md border border-line bg-surface px-2 py-1 text-xs text-fg shadow-lg group-hover:block">
              <span className="font-medium">{formatDate(d.day)}</span>
              <br />
              {n(d.credits)} credits · {n(d.calls)} calls · {n(d.users)} users
            </div>
          </div>
        ))}
      </div>
      <figcaption className="flex justify-between text-xs text-faint">
        <span>{formatDate(daily[0]?.day)}</span>
        <span>Peak {n(max === 1 && !daily.some((d) => d.credits) ? 0 : max)} credits a day</span>
        <span>{formatDate(daily.at(-1)?.day)}</span>
      </figcaption>
    </figure>
  );
}

function WorkspaceTable({ title, rows }: { title: string; rows: AdminWorkspace[] }) {
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="w-full text-sm">
        <caption className="pb-1 text-left text-xs font-medium text-muted">{title}</caption>
        <thead>
          <tr className="text-left text-xs text-muted">
            <th className="py-1.5 pr-4 font-medium">Workspace</th>
            <th className="py-1.5 pr-4 text-right font-medium">Storage</th>
            <th className="py-1.5 pr-4 text-right font-medium">Last 24 hours</th>
            <th className="py-1.5 text-right font-medium">Contracts</th>
          </tr>
        </thead>
        <tbody className="tabular-nums">
          {rows.map((w) => (
            <tr key={w.id} className="border-t border-line">
              <td className="py-2 pr-4">
                <span className="block text-fg">
                  {w.name} {w.suspended && <Badge tone="bad">Suspended</Badge>}
                </span>
                <span className="block text-xs text-faint">{w.ownerEmail ?? "No admin"}</span>
              </td>
              <td className="py-2 pr-4 text-right">
                {formatBytes(w.storageBytes)} of {formatBytes(w.storageQuotaBytes)}
              </td>
              <td className="py-2 pr-4 text-right">{formatBytes(w.uploadedLast24hBytes)}</td>
              <td className="py-2 text-right">{n(w.contracts)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Overview({ overview, gate }: { overview: AdminOverview; gate: GateMetrics | null }) {
  const { totals, ai } = overview;
  const windows = [
    ["Last 24 hours", ai.last24h],
    ["Last 7 days", ai.last7d],
    ["Last 30 days", ai.last30d],
  ] as const;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat label="Users" value={n(totals.users)} hint={`${n(totals.usersWithGrants)} with extra credits`} />
        <Stat label="Workspaces" value={n(totals.orgs)} />
        <Stat label="Contracts" value={n(totals.contracts)} />
        <Stat label="Documents" value={n(totals.documents)} />
        <Stat label="Storage" value={formatBytes(totals.storageBytes)} />
      </div>

      <Card>
        <CardTitle description="What AI reading costs us. 1 credit is ₹0.01 of model cost.">AI usage</CardTitle>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="py-1.5 pr-4 font-medium">Period</th>
                <th className="py-1.5 pr-4 text-right font-medium">Calls</th>
                <th className="py-1.5 pr-4 text-right font-medium">Users</th>
                <th className="py-1.5 pr-4 text-right font-medium">Credits</th>
                <th className="py-1.5 pr-4 text-right font-medium">Our cost</th>
                <th className="py-1.5 text-right font-medium">At {ai.priceMultiplier}× price</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {windows.map(([label, w]) => (
                <tr key={label} className="border-t border-line">
                  <td className="py-2 pr-4 text-fg">{label}</td>
                  <td className="py-2 pr-4 text-right">{n(w.calls)}</td>
                  <td className="py-2 pr-4 text-right">{n(w.users)}</td>
                  <td className="py-2 pr-4 text-right">{n(w.credits)}</td>
                  <td className="py-2 pr-4 text-right">{inr(w.costInr)}</td>
                  <td className="py-2 text-right text-muted">{inr(w.costInr * ai.priceMultiplier)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-5">
          <DailyChart daily={ai.daily} />
        </div>
        <p className={cx("mt-4 text-sm", ai.budget.spentCredits >= ai.budget.budgetCredits ? "font-medium text-bad" : "text-muted")}>
          Today (UTC): {n(ai.budget.spentCredits)} of the {n(ai.budget.budgetCredits)}-credit daily ceiling ({inr(ai.budget.budgetCredits / 100)}). Past it, AI
          pauses for everyone until midnight UTC.
        </p>
      </Card>

      <Card>
        <CardTitle description="Find a workspace's owner in the user list below to change its storage or suspend it.">Workspaces</CardTitle>
        <WorkspaceTable title="Most storage" rows={overview.workspaces.biggest} />
        {overview.workspaces.growing.length > 0 && <WorkspaceTable title="Uploaded most in the last 24 hours" rows={overview.workspaces.growing} />}
      </Card>

      {gate && (
        <Card>
          <CardTitle description="Phase 1 ends when both are met.">Phase 1 gate</CardTitle>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Stat
              label="Weekly active workspaces"
              value={n(gate.gate.weeklyActiveOrgs.value)}
              hint={`Target ${n(gate.gate.weeklyActiveOrgs.target)} · ${gate.gate.weeklyActiveOrgs.met ? "met" : "not met yet"}`}
            />
            <Stat
              label="Activation"
              value={gate.gate.activation.rate === null ? "–" : `${Math.round(gate.gate.activation.rate * 100)}%`}
              hint={`${gate.gate.activation.activated} of ${gate.gate.activation.cohort} new workspaces · target ${Math.round(gate.gate.activation.target * 100)}%`}
            />
          </div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted">
                  <th className="py-1.5 pr-4 font-medium">Week ending</th>
                  <th className="py-1.5 pr-4 text-right font-medium">Active</th>
                  <th className="py-1.5 pr-4 text-right font-medium">New</th>
                  <th className="py-1.5 pr-4 text-right font-medium">Contracts</th>
                  <th className="py-1.5 pr-4 text-right font-medium">Reminders</th>
                  <th className="py-1.5 text-right font-medium">Acted on</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {gate.weeks.map((w) => (
                  <tr key={w.to} className="border-t border-line">
                    <td className="py-2 pr-4 text-fg">{formatDate(w.to.slice(0, 10))}</td>
                    <td className="py-2 pr-4 text-right">{n(w.activeOrgs)}</td>
                    <td className="py-2 pr-4 text-right">{n(w.newOrgs)}</td>
                    <td className="py-2 pr-4 text-right">{n(w.contractsCreated)}</td>
                    <td className="py-2 pr-4 text-right">{n(w.remindersSent)}</td>
                    <td className="py-2 text-right">{n(w.deadlinesActedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>
  );
}

const whole = (v: string) => (v.trim() === "" ? null : Number(v));

/** The user's workspace: its storage limit, and suspending or restoring it. */
function WorkspaceControls({ user, onChange }: { user: AdminUser; onChange: (users: AdminUser[]) => void }) {
  const org = user.org!;
  const [quota, setQuota] = useState(org.customQuota ? String(+(org.storageQuotaBytes / GB).toFixed(2)) : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const run = async (body: Parameters<typeof updateWorkspace>[1], message: string) => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      onChange(await updateWorkspace(org.id, body));
      setDone(message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const saveQuota = (e: FormEvent) => {
    e.preventDefault();
    const bytes = gbToBytes(quota);
    run({ storageQuotaBytes: bytes }, bytes === null ? "Back to the default storage limit." : `Storage limit set to ${formatBytes(bytes)}.`);
  };
  return (
    <div className="space-y-3 border-t border-line pt-4">
      <p className="text-sm font-medium text-fg">Workspace · {org.name}</p>
      {error && <Alert tone="bad">{error}</Alert>}
      {done && <Alert tone="good">{done}</Alert>}
      <p className="text-sm text-muted">
        {formatBytes(org.storageBytes)} of {formatBytes(org.storageQuotaBytes)} used{org.customQuota ? " (its own limit)" : ""}.
      </p>
      <form onSubmit={saveQuota} className="space-y-2">
        <div className="flex items-end gap-2">
          <Field label="Storage limit (GB)">
            {(id) => (
              <TextInput id={id} type="number" min={0} step="any" inputMode="decimal" placeholder="Default" value={quota} onChange={(e) => setQuota(e.target.value)} />
            )}
          </Field>
          <Button type="submit" disabled={busy}>
            Save storage
          </Button>
        </div>
        <p className="text-xs text-faint">Empty means the default. Only the files people upload count, not the text and search index we keep.</p>
      </form>
      {org.suspended ? (
        <div className="space-y-2">
          <Alert tone="bad">
            Suspended on {formatDate(org.suspended.at.slice(0, 10))}
            {org.suspended.by ? ` by ${org.suspended.by}` : ""}
            {org.suspended.reason ? `: ${org.suspended.reason}` : "."} Its people can view and export, but not change or upload anything.
          </Alert>
          <Button variant="secondary" disabled={busy} onClick={() => run({ suspended: false }, "Workspace restored.")}>
            Restore workspace
          </Button>
        </div>
      ) : (
        <div className="flex items-end gap-2">
          <Field label="Reason for suspending">
            {(id) => <TextInput id={id} value={reason} maxLength={200} placeholder="e.g. uploading junk files" onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <Button variant="danger" disabled={busy} onClick={() => run({ suspended: true, reason }, "Workspace suspended.")}>
            Suspend
          </Button>
        </div>
      )}
    </div>
  );
}

/** Extra credits for today, raised limits for a while or for good, the grants in force, and the workspace. */
function ManageUser({ user, onChange, onClose }: { user: AdminUser; onChange: (users: AdminUser[]) => void; onClose: () => void }) {
  const [bonus, setBonus] = useState("100");
  const [daily, setDaily] = useState(String(user.limits.override?.dailyLimit ?? user.limits.daily - user.limits.bonusToday));
  const [monthly, setMonthly] = useState(String(user.limits.override?.monthlyLimit ?? user.limits.monthly - user.limits.bonusMonth));
  const [duration, setDuration] = useState<GrantDuration>("week");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const run = async (what: () => Promise<AdminUser>, message: string) => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      onChange([await what()]);
      setDone(message);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const give = (e: FormEvent) => {
    e.preventDefault();
    const credits = whole(bonus) ?? 0;
    run(() => addGrant(user.id, { kind: "bonus", credits }), `Added ${n(credits)} credits for today.`);
  };
  const raise = (e: FormEvent) => {
    e.preventDefault();
    const body: GrantRequest = {
      kind: "limits",
      dailyLimit: whole(daily),
      monthlyLimit: whole(monthly),
      duration,
    };
    run(() => addGrant(user.id, body), `New limits saved: ${DURATION_LABELS[duration].toLowerCase()}.`);
  };

  return (
    <Dialog open title={`Manage · ${user.email}`} onClose={onClose}>
      <div className="grid gap-4">
        <CreditBar label="Today" w={user.credits.day} resets={user.limits.bonusToday ? `Includes ${n(user.limits.bonusToday)} extra.` : "Refills tomorrow."} />
        <CreditBar label="This month" w={user.credits.month} resets={`Refills on ${formatDate(user.credits.month.resetsOn)}.`} />
      </div>
      {error && <Alert tone="bad">{error}</Alert>}
      {done && <Alert tone="good">{done}</Alert>}

      <form onSubmit={give} className="space-y-2 border-t border-line pt-4">
        <p className="text-sm font-medium text-fg">Add credits for today</p>
        <div className="flex items-end gap-2">
          <Field label="Extra credits">
            {(id) => <TextInput id={id} type="number" min={1} step={1} inputMode="numeric" value={bonus} onChange={(e) => setBonus(e.target.value)} required />}
          </Field>
          <Button type="submit" disabled={busy}>
            Add
          </Button>
        </div>
        <p className="text-xs text-faint">On top of today's limit, and counted towards this month's too. Unused ones end tonight.</p>
      </form>

      <form onSubmit={raise} className="space-y-2 border-t border-line pt-4">
        <p className="text-sm font-medium text-fg">Raise daily and monthly limits</p>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Daily limit">
            {(id) => <TextInput id={id} type="number" min={0} step={1} inputMode="numeric" value={daily} onChange={(e) => setDaily(e.target.value)} />}
          </Field>
          <Field label="Monthly limit">
            {(id) => <TextInput id={id} type="number" min={0} step={1} inputMode="numeric" value={monthly} onChange={(e) => setMonthly(e.target.value)} />}
          </Field>
        </div>
        <div className="flex items-end gap-2">
          <Field label="For">
            {(id) => (
              <Select id={id} value={duration} onChange={(e) => setDuration(e.target.value as GrantDuration)}>
                {(Object.keys(GRANT_DURATIONS) as GrantDuration[]).map((d) => (
                  <option key={d} value={d}>
                    {DURATION_LABELS[d]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Button type="submit" disabled={busy}>
            Save limits
          </Button>
        </div>
        <p className="text-xs text-faint">Leave one empty to keep the free limit. The newest limits win, so you can also lower them.</p>
      </form>

      <div className="space-y-2 border-t border-line pt-4">
        <p className="text-sm font-medium text-fg">In force</p>
        {user.grants.length === 0 ? (
          <p className="text-sm text-muted">Free limits only.</p>
        ) : (
          <ul className="space-y-2">
            {user.grants.map((g) => (
              <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line px-3 py-2 text-sm">
                <span>
                  <span className="block text-fg">{grantLine(g, user.today)}</span>
                  <span className="block text-xs text-faint">{g.grantedBy === EARLY_USERS.grantedBy ? "Early-user campaign" : `by ${g.grantedBy}`}</span>
                </span>
                <span className="flex gap-1.5">
                  {g.kind === "limits" && g.endsOn !== null && (
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => run(() => makePermanent(user.id, g.id), "Those limits are now permanent.")}
                    >
                      Make permanent
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => run(() => revokeGrant(user.id, g.id), "Removed.")}>
                    Remove
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {user.org && <WorkspaceControls key={user.org.id} user={user} onChange={onChange} />}
      <div className="flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      </div>
    </Dialog>
  );
}

function Users() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      searchUsers(q).then(
        (u) => live && (setUsers(u), setError(null)),
        (err) => live && setError(errorText(err)),
      );
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q]);
  const replace = (changed: AdminUser[]) => setUsers((list) => list?.map((x) => changed.find((u) => u.id === x.id) ?? x) ?? null);
  const selected = users?.find((u) => u.id === open);

  return (
    <Card>
      <CardTitle description="Recent AI users first. Search by email or name.">Users</CardTitle>
      <div className="mt-4">
        <TextInput type="search" placeholder="Search users" aria-label="Search users" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {error && (
        <div className="mt-3">
          <Alert tone="bad">{error}</Alert>
        </div>
      )}
      {!users ? (
        <div className="mt-4">
          <Spinner />
        </div>
      ) : users.length === 0 ? (
        <p className="mt-4 text-sm text-muted">No users match.</p>
      ) : (
        <ul className="mt-3 divide-y divide-line">
          {users.map((u) => {
            const early = u.grants.some((g) => g.grantedBy === EARLY_USERS.grantedBy);
            const raised = u.grants.some((g) => g.kind === "limits" && g.grantedBy !== EARLY_USERS.grantedBy);
            return (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-fg">
                    {u.email}
                    {u.name && <span className="font-normal text-muted"> · {u.name}</span>}
                  </p>
                  <p className="text-xs text-faint">
                    {u.org?.name ?? "No workspace"} · {n(u.contracts)} contract{u.contracts === 1 ? "" : "s"} · last AI use {u.lastAiAt ? formatDate(u.lastAiAt.slice(0, 10)) : "never"}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {u.org?.suspended && <Badge tone="bad">Suspended</Badge>}
                  {early && <Badge tone="good">Early user</Badge>}
                  {raised && <Badge tone="info">Raised limits</Badge>}
                  {u.limits.bonusToday > 0 && <Badge tone="good">+{n(u.limits.bonusToday)} today</Badge>}
                  <span className={cx("text-xs tabular-nums", u.credits.day.remaining === 0 ? "text-bad" : "text-muted")}>
                    Today {n(u.credits.day.used)}/{n(u.credits.day.limit)} · Month {n(u.credits.month.used)}/{n(u.credits.month.limit)}
                  </span>
                  <Button size="sm" variant="secondary" onClick={() => setOpen(u.id)}>
                    Manage
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {selected && <ManageUser user={selected} onChange={replace} onClose={() => setOpen(null)} />}
    </Card>
  );
}

export function Admin() {
  const { me } = useSession();
  const online = useOnline();
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [gate, setGate] = useState<GateMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!online || !me?.operator) return;
    getOverview().then(setOverview, (err) => setError(errorText(err)));
    getGate().then(setGate, () => setGate(null));
  }, [online, me?.operator]);

  if (!me?.operator) {
    return (
      <div className="space-y-2">
        <h1 className="text-xl font-semibold tracking-tight">Page not found</h1>
        <a className="font-medium text-accent-fg underline underline-offset-2" href="/">
          Go to the dashboard
        </a>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PageHeader title="Admin" description="Everyone's usage, each user's AI credits, and workspace storage. Only people in OPERATOR_EMAILS see this page." />
      {!online && <Alert tone="warn">Connect to the internet to see the admin dashboard.</Alert>}
      {error && <Alert tone="bad">{error}</Alert>}
      {overview ? <Overview overview={overview} gate={gate} /> : online && !error && <Spinner />}
      {online && <Users />}
    </div>
  );
}
